import {
  DynamicQuestions,
  GetDynamicQuestionsDocument,
  QueryGetDynamicQuestionsArgs,
  BasicPolicyQuestion,
  PriorClaimsQuestion,
  GetDynamicQuestionsQuery,
} from "@covertree/new-business-graphql";

import { createAsyncThunkSlice, typedReducerCreator } from "../../utils";

interface DynamicQuestionsState {
  basicPolicyQuestions: { [fieldName: string]: BasicPolicyQuestion };
  priorClaimsQuestions: { [fieldName: string]: PriorClaimsQuestion };
  error?: Error;
  loading: boolean;
}

const initialState: DynamicQuestionsState = {
  basicPolicyQuestions: {},
  priorClaimsQuestions: {},
  loading: false,
};

export const dynamicQuestionsSlice = createAsyncThunkSlice({
  name: "dynamicQuestions",
  initialState,
  selectors: {
    /**
     * Returns whether the slice is loading
     * @param state
     */
    selectDynamicQuestionsLoading: (state) => state.loading,
    /**
     * Returns map of Basic Policy Questions
     * @param state
     */
    selectBasicQuestions: (state) => state.basicPolicyQuestions,
    /**
     * Returns map of Prior Claims Questions
     * @param state
     */
    selectPriorClaimsQuestions: (state) => state.priorClaimsQuestions,
  },
  reducers: (c) => {
    const { createWithType, create } = typedReducerCreator(c);

    return {
      /**
       * Fills Dynamic Questions into state
       */
      getDynamicQuestions: createWithType<
        DynamicQuestions,
        QueryGetDynamicQuestionsArgs
      >(
        async (args, thunkAPI) => {
          const { data, error } =
            await thunkAPI.extra.client.query<GetDynamicQuestionsQuery>({
              query: GetDynamicQuestionsDocument,
              variables: args,
            });

          if (error) {
            return thunkAPI.rejectWithValue(error);
          }

          return data.getDynamicQuestions;
        },
        {
          pending: (state) => {
            state.loading = true;
          },
          fulfilled: (
            state,
            { payload: { basicPolicyQuestions, priorClaimsQuestions } }
          ) => {
            state.error = undefined;

            state.basicPolicyQuestions = basicPolicyQuestions.reduce(
              (acc, val) => ({
                ...acc,
                [val.fieldName]: val,
              }),
              {}
            );
            state.priorClaimsQuestions = priorClaimsQuestions.reduce(
              (acc, val) => ({
                ...acc,
                [val.fieldName]: val,
              }),
              {}
            );
          },
          rejected: (state, { payload }) => {
            state.error = payload as Error;
            state.basicPolicyQuestions = {};
            state.priorClaimsQuestions = {};
          },
          settled: (state) => {
            state.loading = false;
          },
        }
      ),
      /**
       * Sets Dynamic Questions
       */
      setDynamicQuestions: create.reducer<DynamicQuestions>(
        (state, { payload }) => {
          state.basicPolicyQuestions = payload.basicPolicyQuestions.reduce(
            (acc, val) => ({
              ...acc,
              [val.fieldName]: val,
            }),
            {}
          );
          state.priorClaimsQuestions = payload.priorClaimsQuestions.reduce(
            (acc, val) => ({
              ...acc,
              [val.fieldName]: val,
            }),
            {}
          );
        }
      ),
    };
  },
});

export const {
  actions: { getDynamicQuestions, setDynamicQuestions },
  selectors: {
    selectDynamicQuestionsLoading,
    selectBasicQuestions,
    selectPriorClaimsQuestions,
  },
} = dynamicQuestionsSlice;
